import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { PlainTooltip, Dropdown, EllipsisDropdownItem } from "@similarweb/ui-components";
import { CrmManagementDisabledTooltip } from "app/pages/sales-intelligence/common-components/CrmManagementDisabledTooltip";
import InfoIconTooltip from "app/pages/sales-intelligence/common-components/tooltip/InfoIconTooltip/InfoIconTooltip";
import { useCrmManagementReadOnly } from "app/pages/sales-intelligence/hooks/useCrmManagementReadOnly";
import {
    StyledArrow,
    StyledIntegrationTabContainer,
    StyledSettingTitle,
    StyledWrapperContent,
    StyledIntegrationInfo,
    StyledExcludeSelectWithLabel,
    StyledSelectedFields,
    StyledDescription,
} from "app/pages/sales-intelligence/pages/manage-integration/styles/styles";
import {
    MANAGEMENT_INTEGRATION_EXCLUDE_TOOLTIP,
    MANAGEMENT_INTEGRATION_EXCLUDE_DETAILS,
    MANAGEMENT_INTEGRATION_EXCLUDE_DROPDOWN_SEARCH_PLACEHOLDER,
    MANAGEMENT_INTEGRATION_LABEL_TOOLTIP,
    MANAGEMENT_INTEGRATION_EXCLUDE_TITLE,
} from "../../../../common-components/export-table/translations";
import { allTrackers } from "app/services/track/track";
import type { Integration } from "app/pages/sales-intelligence/sub-modules/management/types";
import { WrappedWithPreloader } from "app/pages/sales-intelligence/pages/management/components/common/WrappedWithPreloader/WrappedWithPreloader";
import { useCrmFiltersProps } from "./useCrmFiltersProps";
import withAccountExclusionRequests from "../../hocs/withAccountExclusionRequests";
import { StyledFieldsCount, StyledOnOffSwitch } from "./style";
import { translate } from "app/filters/i18nFilter";
import { getCrmName } from "app/pages/sales-intelligence/utils/crm";
import { FooterButton } from "pro-features/components/MultiCategoriesChipDown/src/MultiCategoryChipdownStyles";
import salesSettingsHelper from "app/pages/sales-intelligence/services/salesSettingsHelper";
import { haveSameElements } from "app/pages/sales-intelligence/utils/arrays";

const CrmFiltersSettings = ({ integrationName }: { integrationName: Integration }) => {
    const { isReadOnly } = useCrmManagementReadOnly();

    const {
        accountFields,
        isLoadingAccountFields,
        integrationStatus,
        putIntegrationStatus,
        putFilterFields,
        filterFields,
        isFilterFieldsLoading,
    } = useCrmFiltersProps(integrationName);

    const [selectedNames, setSelectedNames] = useState<string[]>(filterFields);

    useEffect(() => {
        setSelectedNames(filterFields);
    }, [filterFields]);

    const hasChanged = useMemo(() => {
        return !haveSameElements(selectedNames, filterFields);
    }, [filterFields, selectedNames]);

    const [isOpen, setIsOpen] = useState(false);

    const dropdownRef = useRef(null);

    const fieldsLimit = salesSettingsHelper.crmFilterFieldsLimit();

    const handleSelect = (selectedItem: { id: string }) => {
        const selectedName = selectedItem.id;

        if (selectedNames.includes(selectedName)) {
            setSelectedNames(selectedNames.filter((name) => name !== selectedName));
        } else {
            setSelectedNames(selectedNames.concat(selectedName));
        }
    };

    const handleApply = () => {
        dropdownRef.current?.popupClickRef.closePopup();
        putFilterFields(selectedNames);
        allTrackers.dispatchTrackEvent(
            `${integrationName} integration`,
            "refine by",
            `exclude/${integrationName} accounts/map refine`,
        );
    };

    const handleToggle = () => {
        const status = integrationStatus ? "off" : "on";
        allTrackers.dispatchTrackEvent(
            `${integrationName} integration`,
            `${integrationStatus ? "enable" : "disable"} exclude`,
            `exclude/${integrationName} accounts/${status}`,
        );

        putIntegrationStatus(!integrationStatus);
    };

    const renderDropdownButton = useCallback(() => {
        const currentField = accountFields.find((field) => {
            return field.name === selectedNames[0];
        });

        let buttonLabel = currentField?.label ?? selectedNames[0];
        if (selectedNames.length > 1) {
            buttonLabel = `${buttonLabel} +${selectedNames.length - 1}`;
        }

        return (
            <WrappedWithPreloader
                key="field-statusDD-key-loader"
                isLoading={isFilterFieldsLoading || isLoadingAccountFields}
                width={100}
                height={15}
            >
                <StyledSelectedFields
                    isDisabled={!integrationStatus || isReadOnly}
                    isSelected={!isOpen}
                    id={selectedNames[0]}
                >
                    {buttonLabel}
                    <StyledArrow>&#9662;</StyledArrow>
                </StyledSelectedFields>
            </WrappedWithPreloader>
        );
    }, [
        accountFields,
        selectedNames,
        isFilterFieldsLoading,
        isLoadingAccountFields,
        integrationStatus,
        isReadOnly,
        isOpen,
    ]);

    const renderDropdownItems = useCallback(() => {
        const initiallySelectedItems = filterFields;
        const notInitiallySelectedItems = accountFields
            .map((field) => field.name)
            .filter((name) => !initiallySelectedItems.includes(name));

        const renderDropdownItem = (name: string) => {
            const isSelected = selectedNames.includes(name);
            const isOutOfLimit = selectedNames.length >= fieldsLimit && !isSelected;
            const label = accountFields.find((field) => field.name === name)?.label ?? name;

            return (
                <EllipsisDropdownItem
                    selected={isSelected}
                    disabled={isOutOfLimit}
                    id={name}
                    key={name}
                    showCheckBox
                >
                    {label}
                </EllipsisDropdownItem>
            );
        };

        return [
            renderDropdownButton(),
            ...initiallySelectedItems.map(renderDropdownItem),
            ...notInitiallySelectedItems.map(renderDropdownItem),
        ];
    }, [filterFields, accountFields, renderDropdownButton, selectedNames, fieldsLimit]);

    const handleDropdownToggle = (isOpen: boolean) => {
        setIsOpen(isOpen);
        if (!isOpen) {
            setSelectedNames(filterFields);
        }
    };

    const renderToggle = (readOnly: boolean) => (
        <PlainTooltip
            enabled={!readOnly && !integrationStatus}
            tooltipContent={translate(MANAGEMENT_INTEGRATION_EXCLUDE_TOOLTIP[integrationName])}
        >
            <StyledOnOffSwitch
                isSelected={integrationStatus}
                onClick={handleToggle}
                isDisabled={readOnly}
            />
        </PlainTooltip>
    );

    return (
        <StyledIntegrationTabContainer>
            <StyledSettingTitle>
                {translate(MANAGEMENT_INTEGRATION_EXCLUDE_TITLE, {
                    crm: getCrmName(integrationName),
                })}
            </StyledSettingTitle>
            <StyledWrapperContent>
                <CrmManagementDisabledTooltip renderContent={renderToggle} />
                <StyledDescription>
                    <StyledIntegrationInfo>
                        {translate(MANAGEMENT_INTEGRATION_EXCLUDE_DETAILS[integrationName])}
                        &nbsp;
                    </StyledIntegrationInfo>
                    <StyledExcludeSelectWithLabel className="exclude-container">
                        <Dropdown
                            onToggle={handleDropdownToggle}
                            hasSearch
                            appendTo=".exclude-container"
                            width={320}
                            selectedIds={selectedNames.reduce(
                                (acc, field) => {
                                    acc[field] = true;
                                    return acc;
                                },
                                {} as Record<string, boolean>,
                            )}
                            onClick={handleSelect}
                            disabled={!integrationStatus || isFilterFieldsLoading || isReadOnly}
                            dropdownPopupPlacement="bottom-left"
                            searchPlaceHolder={translate(
                                MANAGEMENT_INTEGRATION_EXCLUDE_DROPDOWN_SEARCH_PLACEHOLDER[integrationName],
                            )}
                            closeOnItemClick={false}
                            footerComponent={() => (
                                <FooterButton
                                    onClick={handleApply}
                                    disabled={!hasChanged || !selectedNames.length || isReadOnly}
                                >
                                    {translate("common.apply")}
                                </FooterButton>
                            )}
                            headerComponent={
                                <StyledFieldsCount>
                                    {selectedNames.length}/{fieldsLimit}&nbsp;
                                    {translate("si.common.fields")}
                                </StyledFieldsCount>
                            }
                            ref={dropdownRef}
                        >
                            {renderDropdownItems()}
                        </Dropdown>
                        &nbsp;&nbsp;
                        <InfoIconTooltip tooltipKey={MANAGEMENT_INTEGRATION_LABEL_TOOLTIP} />
                    </StyledExcludeSelectWithLabel>
                </StyledDescription>
            </StyledWrapperContent>
        </StyledIntegrationTabContainer>
    );
};

export default withAccountExclusionRequests(CrmFiltersSettings);
